// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Warm water, a drop of washing up liquid and a soft brush will safely clean most solid gold and most hard stones. That is the whole method for the easy cases. The reason this guide is long is the pieces where that method destroys value in seconds.

## Before you touch it, ask three questions

**Is the back of the setting open or closed?** Hold the piece up to a light. If light passes through behind the stone, the setting is open and water can drain out. If the back is sealed metal, stop. Closed backs on Georgian and early Victorian pieces often hide foil behind the stone, a thin layer of coloured metal that boosts brightness. Water gets under it, the foil tarnishes, and the stone goes cloudy and grey. That damage is permanent and expensive.

**What is the stone?** Hardness is not the issue. Porosity and fractures are. The stones that fail are the ones that drink liquid or already have cracks running to the surface.

**Is there anything organic in it?** Pearl, coral, amber, jet, ivory, tortoiseshell and hair work are all once-living materials. They react to detergent, alcohol and ultrasonic vibration in ways minerals do not.

## The safe method for ordinary pieces

This works for solid gold, platinum, and hard stones such as diamond, sapphire, ruby, spinel, garnet, topaz and quartz, in open settings.

1. Line a bowl with a soft cloth, in case you drop something. Do not work over an open sink.
2. Fill it with warm, not hot, water and add one drop of plain washing up liquid.
3. Soak for a few minutes. Longer will not help.
4. Brush gently with a soft toothbrush, paying attention to the back of the setting where dirt collects and dulls the stone.
5. Rinse in clean water. Dry with a lint free cloth, then leave it on a towel for an hour so the settings dry out completely.

On solid metal with no stones in it at all, a plain chain, a wedding band, a bangle, an ultrasonic cleaner is better than any of this: it reaches inside links no brush can enter and it does nothing to the metal. Two cautions even there. Hollow chains and bangles trap water that is hard to drive out again, and on engraved antique silver the machine lifts the dark out of the recesses along with the dirt, which is contrast worth keeping. Everything in the next section is about what happens when there is something other than metal in the piece.

Most of the sparkle people think they have lost is a film of soap, skin oil and hairspray on the **back** of the stone. Light enters through the top and should bounce back off the pavilion. A greasy pavilion swallows it.

## Never put these in an ultrasonic cleaner

An ultrasonic works by making microscopic bubbles collapse against the surface. Anything with a crack, a fracture, a filling or a porous structure can be shaken apart.

| Do not clean | Why |
| --- | --- |
| Emerald | Almost always oiled or resin filled. Ultrasonic strips the filling and reveals every fracture. |
| Opal | Contains water. Vibration and heat cause crazing, a web of fine cracks. Doublets and triplets can separate. |
| Pearl | Soft, porous, and the nacre lifts. The drilled hole lets liquid into the core. |
| Turquoise, lapis, malachite | Porous and usually treated or dyed. Colour lifts out. |
| Coral, amber, jet, ivory | Organic, soft and often waxed. |
| Tanzanite, kunzite, iolite | Cleave easily. A knock or a shock can split them along a plane. |
| Anything foiled or closed backed | Water reaches the foil. |
| Loose or repaired settings | The vibration finishes the job. |

Steam cleaners have the same problem plus heat. Heat is what drives oil out of an emerald.

## What household advice gets wrong

**Toothpaste.** It is an abrasive. It scratches gold, and on silver it dulls the surface into a flat haze.

**Baking soda and foil.** The classic silver trick works by chemical reaction, and it works too well. It strips tarnish from everywhere at once, including the darkened recesses that give an engraved or chased pattern its depth. On antique silver that is a permanent flattening. Our [guide to silver marks](/blog/silver-hallmarks-explained) has more on why polishing a hallmark is a bad idea.

**Vinegar or lemon juice.** Acid. It attacks the alloy metals in low karat gold, damages pearls immediately, and eats limestone based stones.

**Boiling water.** Thermal shock cracks stones and loosens settings.

**Ammonia based window cleaner.** Fine on a bare diamond, bad on almost everything else, and it will lift the polish on softer stones.

## Pearls, specifically

Pearls need the opposite of a soak. Wipe each pearl with a soft cloth slightly damp with plain water after wearing, and let them dry flat before putting them away. Perfume, hairspray and sun cream all attack nacre, so pearls go on last when you dress and come off first.

Never hang a pearl necklace to store it, because the silk stretches. Restring every year or two if you wear them often, and ask for knots between the pearls so a broken string does not scatter the lot. If you are buying, our [guide to telling real pearls from imitations](/blog/real-vs-fake-pearls) covers what to check first.

## Silver, and when not to remove tarnish

Tarnish is a chemical reaction with sulphur in the air, not dirt. On a plain, bright piece, remove it with a soft cloth and a mild silver polish, working gently.

On a chased, engraved or oxidised piece, leave the dark recesses alone. That contrast is part of the design and, on antique work, part of the value. A dealer can tell instantly when a piece has been dipped, and it lowers the price.

Store silver in a closed bag with an anti-tarnish strip. Air is what causes it.

## Gold, and what wear tells you

Solid gold does not tarnish. If a gold coloured piece is going dull or showing another colour at the edges, you may be looking at plating wearing through rather than dirt. Our [guide to gold marks](/blog/how-to-tell-if-gold-jewelry-is-real) explains how to tell.

Resist polishing an antique gold piece bright. A hundred years of soft, even wear is called patina, and collectors pay for it. Machine polishing removes metal, softens crisp engraving, and can round off a hallmark until it is unreadable.

## When to hand it to a jeweller

- A stone is loose, or rattles when you tap the piece gently near your ear.
- A claw is worn thin, bent or missing a tip.
- The piece is heavily encrusted and you cannot see what is underneath.
- There is any enamel, particularly the translucent kind used in [Art Nouveau work](/blog/art-deco-vs-art-nouveau-jewelry).
- It is going to be sold, and you want the safest possible clean before photographing it.

A professional ultrasonic and steam is the right tool in the right hands, on the right piece. Ask what they intend to do before you leave it, and say clearly if anything is foiled, filled or organic.

## Before you photograph a piece to sell

Clean it, but do not restore it. Buyers of second hand jewelry are reading the piece for evidence of age and honesty. A gently cleaned piece with intact patina photographs better and sells better than one buffed to look new. The [phone photography guide](/blog/how-to-photograph-jewelry-with-your-phone) covers the rest.

If you are selling on Nextmine, say in the listing what you did. "Cleaned with warm soapy water, not polished" is exactly the kind of sentence that makes a collector trust the rest of your description. [See what is listed right now](/s), or read [how payments are protected](/how-payments-work).
`;

export default body;
